// "Missing information" panel (issue 25): what is not known about the studio, what the last
// "Check info" run found (with the page it came from), and what is still missing. A missing social
// platform has a field to paste the profile URL: the app never guesses an address.
import { useState } from 'react';
import { Link } from 'react-router';
import { Empty, ErrorMsg, Section, Time } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { FIELD_LABEL, jobType } from '../../lib/format';
import type { Job, MissingItem, StudioDetail } from '../../lib/types';

interface Found {
  field: string;
  value: string;
  state: string;
  url: string | null;
  how?: string;
}

function PasteProfile({ studioId, item, onAdded }: { studioId: string; item: MissingItem; onAdded: (msg: string) => void }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api<{ outcome: string }>(`/api/studios/${studioId}/socials`, { method: 'POST', body: { url, platform: item.platform } });
      setUrl('');
      onAdded(r.outcome === 'added' ? `${item.label} profile added.` : `${item.label} profile: ${r.outcome.replace(/_/g, ' ')}.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="inline-form" aria-label={`Paste the ${item.label} profile URL`}>
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="paste the profile URL" required aria-label={`${item.label} profile URL`} inputMode="url" />
      <button type="submit" className="btn small" disabled={busy}>
        Add
      </button>
      <ErrorMsg>{error}</ErrorMsg>
    </form>
  );
}

function foundLabel(f: Found): string {
  if (f.field.startsWith('social:')) return `${f.field.slice(7)} profile`;
  if (f.field.startsWith('company_id:')) return `company identifier (${f.field.slice(11)})`;
  return FIELD_LABEL[f.field] ?? f.field;
}

export function MissingInfoPanel({
  studio,
  jobs,
  checking,
  onCheck,
  notify,
  reload,
}: {
  studio: StudioDetail;
  jobs: Job[] | null;
  checking: boolean;
  onCheck: (() => void) | null;
  notify: (msg: string, tone?: 'ok' | 'warn') => void;
  reload: () => void;
}) {
  const last = (jobs ?? []).find((j) => j.type === 'check_info' && ['succeeded', 'partial', 'failed'].includes(j.status));
  const out = (last?.outputs ?? {}) as { found?: Found[]; still_missing?: MissingItem[]; errors?: string[] };
  const found = out.found ?? [];
  const stillMissing = out.still_missing ?? null;
  const fields = studio.missing_info.filter((m) => m.kind === 'field');
  const socials = studio.missing_info.filter((m) => m.kind === 'social');
  return (
    <Section
      title="Missing information"
      aside={
        onCheck && (
          <button type="button" className="btn small" onClick={onCheck} disabled={checking}>
            {checking ? 'Checking…' : 'Check info'}
          </button>
        )
      }
    >
      <div data-testid="missing-info" className="stack-s">
        {fields.length === 0 && socials.length === 0 ? (
          <Empty>Nothing is missing from the list below.</Empty>
        ) : (
          <>
            {fields.length > 0 && (
              <p className="small" data-testid="missing-fields">
                <strong>Not known:</strong> {fields.map((m) => m.label).join(', ')}. City and country are set with Edit in Profile; the other items are read from the website
                by Check info.
              </p>
            )}
            {socials.length > 0 && (
              <ul className="rows" data-testid="missing-socials">
                {socials.map((m) => (
                  <li key={m.key} data-platform={m.platform}>
                    <strong>{m.label}</strong> <span className="muted small">· no account recorded</span>
                    <PasteProfile
                      studioId={studio.id}
                      item={m}
                      onAdded={(msg) => {
                        notify(msg);
                        reload();
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {last && (
          <div className="hypothesis" data-testid="check-result">
            <h3 className="sub">
              Last check: {last.status} · <Time iso={last.finished_at} /> · <Link to={`/jobs/${last.id}`}>job details</Link>
            </h3>
            {last.status === 'failed' && last.error && <p className="small">{last.error}</p>}
            <div data-testid="check-found">
              {found.length === 0 ? (
                <p className="small">Nothing new was found on the pages read.</p>
              ) : (
                <ul className="plain small">
                  {found.map((f, i) => (
                    <li key={i}>
                      <strong>Found</strong> {foundLabel(f)}: {f.value.startsWith('http') ? <a href={f.value} target="_blank" rel="noreferrer noopener">{f.value}</a> : f.value}{' '}
                      <span className="muted">
                        · {f.state === 'proposed' ? 'proposed for review' : 'recorded'}
                        {f.how ? ` · ${f.how}` : ''}
                        {f.url ? ` · ${f.url}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="small" data-testid="check-still-missing">
              <strong>Still missing:</strong> {stillMissing ? (stillMissing.length ? stillMissing.map((m) => m.label).join(', ') : 'nothing') : 'not recorded'}
            </p>
          </div>
        )}
        <p className="muted small">
          {jobType('check_info')} reads this studio&apos;s own website (contact, about, team and legal-notice pages first) and the profile links, <code>rel=me</code> links
          and schema.org <code>sameAs</code> it declares. It never opens LinkedIn, Instagram, Facebook or X, and never builds a profile address from the studio&apos;s name: a
          platform it cannot find stays missing until you paste the URL.
        </p>
      </div>
    </Section>
  );
}
